// Public pages have one URL per language: English at the root (/, /blog, /blog/<slug>), the other
// languages under their code (/fr, /fr/blog, /fr/blog/<slug>). Search engines and AI crawlers only
// see what is at a URL, so a translation without its own URL is invisible to them.
// scripts/prerender-public.mjs writes the same pages as static HTML; keep the two lists in step.
import { hasBlogTranslation } from './blogTranslations';

export const HOME_LANGUAGES = ['fr', 'es', 'pt', 'ar', 'hi', 'ur', 'vi'];   // English is the root
export const BLOG_LANGUAGES = ['fr', 'es'];                                    // where articles are translated

// The language a public URL is in ('/fr/blog' → 'fr'), or null for English / app URLs.
export function urlLanguage(pathname = '') {
  const match = /^\/([a-z]{2})(?=\/|$)/.exec(pathname);
  return match && HOME_LANGUAGES.includes(match[1]) ? match[1] : null;
}

export function withoutLanguage(pathname = '/') {
  const lang = urlLanguage(pathname);
  return lang ? pathname.slice(lang.length + 1) || '/' : pathname;
}

export function localizedPath(path, lang) {
  if (!lang || lang === 'en') return path;
  return `/${lang}${path === '/' ? '' : path}`;
}

// This page's URL in another language, or null when that version does not exist.
export function pathIn(pathname, lang) {
  const base = withoutLanguage(pathname);
  const language = (lang || 'en').split('-')[0];
  if (language === 'en') return base;
  if (base === '/') return HOME_LANGUAGES.includes(language) ? localizedPath('/', language) : null;
  if (base === '/blog') return BLOG_LANGUAGES.includes(language) ? localizedPath('/blog', language) : null;
  const article = /^\/blog\/([^/]+)$/.exec(base);
  if (article) return hasBlogTranslation(article[1], language) ? localizedPath(base, language) : null;
  return null;
}

// Links between public pages: to the reader's language when that version exists, else English.
export function linkFor(path, lang) {
  return pathIn(path, lang) || path;
}

// <link rel="alternate" hreflang> entries for a page: every language it exists in, plus x-default
// (the English page) for readers in any other language.
export function alternatesFor(pathname) {
  const base = withoutLanguage(pathname);
  const versions = ['en', ...HOME_LANGUAGES].map((lang) => ({ lang, path: pathIn(base, lang) })).filter((v) => v.path);
  return versions.length > 1 ? [...versions, { lang: 'x-default', path: base }] : [];
}

// The language public content is shown in: the URL's, else the reader's choice.
export function pageLanguage(pathname, i18n) {
  return urlLanguage(pathname) || (i18n?.resolvedLanguage || i18n?.language || 'en').split('-')[0];
}
